import ReactGA from 'react-ga4';

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

// Google Analytics Measurement-ID wird aus der Umgebungsvariable geladen.
// Siehe .env / .env.example: REACT_APP_GA_MEASUREMENT_ID=G-XXXXXXXXXX
// Fällt auf die fest zugewiesene Measurement-ID zurück, falls keine .env-Datei
// vorhanden ist (z. B. im Produktions-Build ohne gesetzte Umgebungsvariable).
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

let isInitialized = false;

/**
 * Stellt sicher, dass `window.dataLayer`/`window.gtag` bereits existieren,
 * bevor irgendein Consent-Signal gesendet wird (Google Consent Mode).
 * Muss VOR `initGA()` und idealerweise so früh wie möglich beim App-Start
 * aufgerufen werden, damit Google keine Cookies setzt, solange keine
 * Einwilligung vorliegt (z. B. wenn im GA4-Property "Consent Mode"
 * aktiviert ist – dann verweigert Google Cookies standardmäßig, bis ein
 * explizites "granted"-Signal gesendet wurde).
 */
export const initConsentMode = (): void => {
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const gtag = (...args: any[]) => {
    window.dataLayer.push(args);
  };
  window.gtag = window.gtag || gtag;

  window.gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    wait_for_update: 500,
  });
};

/**
 * Aktualisiert den Google-Consent-Status (Consent Mode) entsprechend der
 * Entscheidung im Consent-Manager. Muss aufgerufen werden, bevor/nachdem
 * `initGA()` bzw. `trackPageView()` ausgeführt wird, damit GA4 das
 * `_ga`-Cookie tatsächlich setzt.
 */
export const updateAnalyticsConsent = (granted: boolean): void => {
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    initConsentMode();
  }

  window.gtag('consent', 'update', {
    analytics_storage: granted ? 'granted' : 'denied',
  });

  if (!granted) {
    deleteAnalyticsCookies();
  }
};

/**
 * Löscht bereits gesetzte Google-Analytics-Cookies (`_ga`, `_ga_<MEASUREMENT_ID>`,
 * `_gid`, `_gat`). Wird benötigt, weil ein einmal gesetztes GA-Cookie beim
 * nachträglichen Widerrufen der Einwilligung nicht automatisch vom Browser
 * entfernt wird – ein reines `consent update: denied` verhindert nur das
 * Setzen NEUER Cookies, löscht aber keine bereits vorhandenen.
 */
export const deleteAnalyticsCookies = (): void => {
  const cookieNames = ['_ga', '_gid', '_gat', '_gat_gtag'];
  if (GA_MEASUREMENT_ID) {
    cookieNames.push(`_ga_${GA_MEASUREMENT_ID.replace(/^G-/, '')}`);
  }

  const hostnameParts = window.location.hostname.split('.');
  const domains: string[] = [window.location.hostname];
  for (let i = 1; i < hostnameParts.length - 1; i += 1) {
    domains.push(`.${hostnameParts.slice(i).join('.')}`);
  }
  domains.push(`.${window.location.hostname}`);

  cookieNames.forEach((name) => {
    // Cookie ohne Domain-Angabe löschen (Standardfall, z. B. localhost).
    document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;

    // Zusätzlich für alle möglichen Domain-Varianten löschen, falls das
    // Cookie ursprünglich mit einer expliziten Domain gesetzt wurde.
    domains.forEach((domain) => {
      document.cookie = `${name}=; path=/; domain=${domain}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    });
  });
};

/**
 * Initialisiert Google Analytics (GA4), sofern eine Measurement-ID
 * konfiguriert ist und noch nicht initialisiert wurde.
 */
export const initGA = (): void => {
  if (isInitialized || !GA_MEASUREMENT_ID) {
    return;
  }

  ReactGA.initialize(GA_MEASUREMENT_ID);
  isInitialized = true;
};

/**
 * Gibt zurück, ob Google Analytics bereits initialisiert wurde.
 */
export const isGAInitialized = (): boolean => isInitialized;

/**
 * Trackt einen Seitenaufruf (Pageview) für die übergebene Route.
 */
export const trackPageView = (path: string): void => {
  if (!isInitialized) {
    return;
  }

  ReactGA.send({ hitType: 'pageview', page: path });
};

/**
 * Trackt ein benutzerdefiniertes Event, z. B. das Absenden des Kontaktformulars.
 */
export const trackEvent = (category: string, action: string, label?: string): void => {
  if (!isInitialized) {
    return;
  }

  ReactGA.event({ category, action, label });
};
